import { selectValue } from '@efdigital/ll-addon-ui/modifiers/select-value';
import { buildListConsumedToolDeployments } from '@efdigital/ll-admin-v2/data/builders/lti/lti-consumed-tool-deployments';
import { buildListConsumedTools } from '@efdigital/ll-admin-v2/data/builders/lti/lti-consumed-tools';
import { buildListConsumingPlatforms } from '@efdigital/ll-admin-v2/data/builders/lti/lti-consuming-platforms';
import { buildCreateResourceLink } from '@efdigital/ll-admin-v2/data/builders/lti/lti-resource-links';
import { DataTypeLti } from '@efdigital/ll-admin-v2/data/data-types/lti';
import type {
  LtiConsumedTool,
  LtiConsumedToolDeployment,
  LtiConsumingPlatform,
  LtiResourceLink,
} from '@efdigital/ll-admin-v2/data/types/lti';
import { findXceedTool } from '@efdigital/ll-admin-v2/utils/lti/products/xceed';
import { completeDeployments } from '@efdigital/ll-admin-v2/utils/lti/ready-integration';
import {
  platformPhaseStatus,
  toolPhaseStatus,
} from '@efdigital/ll-admin-v2/utils/lti/setup-status';
import type { ConsumedToolDeployment } from '@efdigital/ll-cdk-lti-registration-api-types';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';
import { Request } from '@warp-drive/ember';
import BsModal from 'ember-bootstrap/components/bs-modal';

interface Signature {
  Args: {
    clientId: string;
    cohortId: string;
    onCreated: () => void;
  };
  Blocks: {
    trigger: [openModal: () => void];
  };
}

export default class LtiCreateResourceLinkModal extends Component<Signature> {
  @service declare store: Store;

  @tracked visible = false;
  @tracked selectedPlatformId = '';
  @tracked selectedDeploymentId = '';
  @tracked platformResourceLinkId = '';
  @tracked errorMessage: string | null = null;
  @tracked isSaving = false;

  openModal = (): void => {
    this.resetForm();
    this.visible = true;
  };

  closeModal = (): void => {
    this.visible = false;
    this.resetForm();
  };

  resetForm = (): void => {
    this.selectedPlatformId = '';
    this.selectedDeploymentId = '';
    this.platformResourceLinkId = '';
    this.errorMessage = null;
    this.isSaving = false;
  };

  completePlatforms = (platforms: LtiConsumingPlatform[]) => {
    return platforms.filter(
      (platform) => platformPhaseStatus(platform) === 'complete'
    );
  };

  readyTool = (tools: LtiConsumedTool[]) => {
    const tool = findXceedTool(tools);
    if (!tool || toolPhaseStatus(tool) !== 'complete') {
      return null;
    }
    return tool;
  };

  readyDeployments = (deployments: LtiConsumedToolDeployment[]) => {
    return completeDeployments(deployments);
  };

  hasMultiple = (items: { length: number }) => {
    return items.length > 1;
  };

  platformIdForQuery = (
    readyPlatforms: LtiConsumingPlatform[],
    selectedPlatformId: string
  ) => {
    if (selectedPlatformId) {
      return selectedPlatformId;
    }

    if (readyPlatforms.length === 1) {
      return readyPlatforms[0]?.id ?? '';
    }

    return '';
  };

  deploymentIdForCreate = (
    readyDeployments: ConsumedToolDeployment[],
    selectedDeploymentId: string
  ) => {
    if (selectedDeploymentId) {
      return selectedDeploymentId;
    }

    if (readyDeployments.length === 1) {
      return readyDeployments[0]?.id ?? '';
    }

    return '';
  };

  setPlatform = (event: Event) => {
    this.selectedPlatformId = (event.target as HTMLSelectElement).value;
    this.selectedDeploymentId = '';
  };

  setDeployment = (event: Event) => {
    this.selectedDeploymentId = (event.target as HTMLSelectElement).value;
  };

  setPlatformResourceLinkId = (event: Event) => {
    this.platformResourceLinkId = (event.target as HTMLInputElement).value;
  };

  submitCreate =
    (platformId: string, toolId: string, deploymentId: string) =>
    (event: Event) => {
      void this.createResourceLink(event, platformId, toolId, deploymentId);
    };

  createResourceLink = async (
    event: Event,
    platformId: string,
    toolId: string,
    deploymentId: string
  ) => {
    event.preventDefault();

    if (!platformId || !toolId || !deploymentId || !this.args.cohortId) {
      return;
    }

    this.isSaving = true;
    this.errorMessage = null;

    try {
      const link = this.store.createRecord<LtiResourceLink>(
        DataTypeLti.ResourceLink,
        {
          platformResourceLinkId: this.platformResourceLinkId,
          toolItemType: 'cohorts',
          toolItemId: this.args.cohortId,
          isActive: true,
        }
      );
      await this.store.request(
        buildCreateResourceLink(link, platformId, toolId, deploymentId)
      );
      this.args.onCreated();
      this.closeModal();
    } catch (error) {
      this.errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to create resource link';
    } finally {
      this.isSaving = false;
    }
  };

  <template>
    {{yield this.openModal to="trigger"}}
    <BsModal
      @open={{this.visible}}
      @onHidden={{this.closeModal}}
      @size="lg"
      @backdropClose={{false}}
      as |modal|
    >
      <modal.header>
        <h2 class="modal-title h5">Create resource link</h2>
      </modal.header>
      <modal.body>
        <Request @query={{buildListConsumingPlatforms @clientId}}>
          <:content as |platforms|>
            {{#let (this.completePlatforms platforms.data) as |readyPlatforms|}}
              {{#if readyPlatforms.length}}
                {{#if (this.hasMultiple readyPlatforms)}}
                  <div class="mb-3">
                    <label class="form-label" for="lti-create-platform">
                      Platform
                    </label>
                    <select
                      id="lti-create-platform"
                      class="form-select"
                      data-test-lti-create-platform
                      {{selectValue this.selectedPlatformId}}
                      {{on "change" this.setPlatform}}
                    >
                      <option value="">Select platform...</option>
                      {{#each readyPlatforms as |platform|}}
                        <option value={{platform.id}}>
                          {{platform.name}}
                        </option>
                      {{/each}}
                    </select>
                  </div>
                {{/if}}

                {{#let
                  (this.platformIdForQuery
                    readyPlatforms this.selectedPlatformId
                  )
                  as |platformId|
                }}
                  {{#if platformId}}
                    <Request @query={{buildListConsumedTools platformId}}>
                      <:content as |tools|>
                        {{#let (this.readyTool tools.data) as |tool|}}
                          {{#if tool.id}}
                            <Request
                              @query={{buildListConsumedToolDeployments
                                platformId
                                tool.id
                              }}
                            >
                              <:content as |deployments|>
                                {{#let
                                  (this.readyDeployments deployments.data)
                                  as |readyDeployments|
                                }}
                                  {{#if readyDeployments.length}}
                                    {{#if (this.hasMultiple readyDeployments)}}
                                      <div class="mb-3">
                                        <label
                                          class="form-label"
                                          for="lti-create-deployment"
                                        >
                                          Deployment
                                        </label>
                                        <select
                                          id="lti-create-deployment"
                                          class="form-select"
                                          data-test-lti-create-deployment
                                          {{selectValue
                                            this.selectedDeploymentId
                                          }}
                                          {{on "change" this.setDeployment}}
                                        >
                                          <option value="">
                                            Select deployment...
                                          </option>
                                          {{#each
                                            readyDeployments
                                            as |deployment|
                                          }}
                                            <option value={{deployment.id}}>
                                              {{deployment.platformDeploymentId}}
                                            </option>
                                          {{/each}}
                                        </select>
                                      </div>
                                    {{/if}}

                                    {{#let
                                      (this.deploymentIdForCreate
                                        readyDeployments
                                        this.selectedDeploymentId
                                      )
                                      as |deploymentId|
                                    }}
                                      {{#if deploymentId}}
                                        {{#if this.errorMessage}}
                                          <div
                                            class="alert alert-danger"
                                            role="alert"
                                          >
                                            {{this.errorMessage}}
                                          </div>
                                        {{/if}}

                                        <form
                                          class="mb-0"
                                          {{on
                                            "submit"
                                            (this.submitCreate
                                              platformId tool.id deploymentId
                                            )
                                          }}
                                        >
                                          <div class="mb-3">
                                            <label
                                              class="form-label"
                                              for="lti-create-resource-link-id"
                                            >
                                              Platform resource link ID
                                            </label>
                                            <input
                                              id="lti-create-resource-link-id"
                                              type="text"
                                              class="form-control font-monospace"
                                              required
                                              value={{this.platformResourceLinkId}}
                                              data-test-lti-create-resource-link-id
                                              {{on
                                                "input"
                                                this.setPlatformResourceLinkId
                                              }}
                                            />
                                            <div class="form-text">
                                              The claim value from the LMS
                                              launch JWT that identifies this
                                              cohort link.
                                            </div>
                                          </div>
                                          <div
                                            class="d-flex justify-content-end"
                                          >
                                            <button
                                              type="submit"
                                              class="btn btn-primary"
                                              disabled={{this.isSaving}}
                                              data-test-lti-create-resource-link-submit
                                            >
                                              {{#if this.isSaving}}
                                                Creating...
                                              {{else}}
                                                Create resource link
                                              {{/if}}
                                            </button>
                                          </div>
                                        </form>
                                      {{else if
                                        (this.hasMultiple readyDeployments)
                                      }}
                                        <p
                                          class="text-muted mb-0"
                                          data-test-lti-create-select-deployment
                                        >
                                          Select a deployment to create a
                                          resource link.
                                        </p>
                                      {{/if}}
                                    {{/let}}
                                  {{else}}
                                    <p class="text-muted mb-0">
                                      This platform has not been configured for
                                      LTI yet. Please contact your
                                      administrator.
                                    </p>
                                  {{/if}}
                                {{/let}}
                              </:content>
                              <:loading>
                                <p class="text-muted mb-0">Loading
                                  deployments...</p>
                              </:loading>
                              <:error as |error retry|>
                                <div class="alert alert-danger" role="alert">
                                  {{error.message}}
                                </div>
                                {{#if retry}}
                                  <button
                                    type="button"
                                    class="btn btn-outline-primary btn-sm"
                                    {{on "click" retry.retry}}
                                  >
                                    Retry
                                  </button>
                                {{/if}}
                              </:error>
                            </Request>
                          {{else}}
                            <p class="text-muted mb-0">
                              This platform has not been configured for LTI yet.
                              Please contact your administrator.
                            </p>
                          {{/if}}
                        {{/let}}
                      </:content>
                      <:loading>
                        <p class="text-muted mb-0">Loading tools...</p>
                      </:loading>
                      <:error as |error retry|>
                        <div class="alert alert-danger" role="alert">
                          {{error.message}}
                        </div>
                        {{#if retry}}
                          <button
                            type="button"
                            class="btn btn-outline-primary btn-sm"
                            {{on "click" retry.retry}}
                          >
                            Retry
                          </button>
                        {{/if}}
                      </:error>
                    </Request>
                  {{else if (this.hasMultiple readyPlatforms)}}
                    <p
                      class="text-muted mb-0"
                      data-test-lti-create-select-platform
                    >
                      Select a platform to create a resource link.
                    </p>
                  {{/if}}
                {{/let}}
              {{else}}
                <div class="alert alert-info mb-0">
                  This client has not been configured for LTI yet. Please
                  contact your administrator.
                </div>
              {{/if}}
            {{/let}}
          </:content>
          <:loading>
            <p class="text-muted mb-0">Loading platforms...</p>
          </:loading>
          <:error as |error retry|>
            <div class="alert alert-danger" role="alert">{{error.message}}</div>
            {{#if retry}}
              <button
                type="button"
                class="btn btn-outline-primary btn-sm"
                {{on "click" retry.retry}}
              >
                Retry
              </button>
            {{/if}}
          </:error>
        </Request>
      </modal.body>
    </BsModal>
  </template>
}
